import type { Numeric, NumericValue } from '@/utils/numeric';
import type { CultivarSalesProfile } from './cultivar';
import type { PickQuarantineOrder } from './nurseryQuarantine';

// nursery.ts — TypeScript types for the Nursery Management module

export type ReadinessMode = 'lead_time' | 'seasonal_window' | 'manual';
export type LeadTimeAnchor = 'production_date' | 'pot_date' | 'plant_date' | 'graft_date' | 'bud_date' | 'last_movement';

/** The kinds of count a crop has in its season, roughest first. */
export type CountKind = 'bud_count' | 'first_count' | 'second_count';

/**
 * What a tree type is for. Propagation stock (cells, liners, seedlings, rooted
 * layers) is grown on into other tree types; its stock items are not offered
 * for sale unless ticked.
 */
export type TreeTypePurpose = 'sale' | 'propagation';

export interface NurseryTreeType {
  id: number;
  code: string;
  name: string;
  stock_type: 'B' | 'P';
  organisation_id: number | null;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  readiness_mode: ReadinessMode;
  lead_time_days: number | null;
  lead_time_anchor: LeadTimeAnchor;
  season_start_month: number | null;
  season_end_month: number | null;
  /** Seasonal: how long trees grow before their first season. */
  min_growing_months: number | null;
  /** Share expected to be lost before sale after each count. */
  loss_pct_bud: NumericValue;
  loss_pct_first: NumericValue;
  loss_pct_second: NumericValue;
  /** sale: finished stock. propagation: cells, liners, seedlings, layers grown on. */
  purpose?: TreeTypePurpose;
  created_at: string;
  updated_at: string;
}

/** A grade a tree type comes in. 1 is the best. */
export interface TreeTypeGrade {
  id?: number;
  tree_type_id: number;
  grade_rank: number;
  name: string;
  description: string | null;
  /** Share of a crop expected at this grade before grading; a tree type's add up to 100. */
  expected_share_pct: number;
}

/** % off the 1st grade's price, for a grade below it. */
export interface GradeDiscount {
  grade_rank: number;
  discount_pct: number;
}

export interface TreeTypeGradesResponse {
  grades: TreeTypeGrade[];
  discounts: GradeDiscount[];
}

export interface TreeTypeUpdatePayload {
  name?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
  readiness_mode?: ReadinessMode;
  lead_time_days?: number | null;
  lead_time_anchor?: LeadTimeAnchor;
  season_start_month?: number | null;
  season_end_month?: number | null;
  min_growing_months?: number | null;
  loss_pct_bud?: number;
  loss_pct_first?: number;
  loss_pct_second?: number;
  purpose?: TreeTypePurpose;
}

export interface NurseryRow {
  id: number;
  organisation_id: number;
  block_id: number;
  row_number: number;
  row_length_meters: Numeric;
  row_spacing_meters: Numeric;
  capacity: number | null;
  notes: string | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  block_name?: string;
  farm_name?: string;
}

/**
 * A plant is a cultivar from the registry at a tree type; merchandise (potting
 * mix, pots, giftware) has neither and is named (Rob, 2026-10-12).
 */
export type StockItemKind = 'plant' | 'merchandise';

/** A stock item that is a plant: it has a cultivar and a tree type. */
export type PlantStockItem = NurseryStockItem & { cultivar_id: number; tree_type_id: number };

/** True for a plant, which is what a lot of trees is placed as. */
export function isPlantItem(item: NurseryStockItem): item is PlantStockItem {
  return item.kind !== 'merchandise' && item.cultivar_id != null && item.tree_type_id != null;
}

export interface NurseryStockItem {
  id: number;
  organisation_id: number;
  /** Absent from an older server: every item was a plant. */
  kind?: StockItemKind;
  /** Merchandise only. */
  name?: string | null;
  /** What one of it is (each, bag, litre). Merchandise only; null reads as each. */
  unit?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  /** The server's name for it, whichever kind. */
  display_name?: string | null;
  /** Null for merchandise. */
  cultivar_id: number | null;
  rootstock_id: number | null;
  /** Null for merchandise. */
  tree_type_id: number | null;
  sku_code: string | null;
  description: string | null;
  website_price: Numeric;
  /** The tree's own barcode: what a retailer's purchase order names it by. */
  barcode?: string | null;
  active: boolean;
  /** Offered for sale: the order sheet, the website, the retailer range. */
  sellable?: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  tree_type_purpose?: TreeTypePurpose;
  cultivar_name?: string;
  cultivar_trade_name?: string;
  cultivar_protection_status?: string;
  /** The cultivar's crop ("Lemon"). Null when the cultivar has none. */
  crop_type_id?: number | null;
  crop_type_name?: string | null;
  rootstock_name?: string;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
}

/**
 * An umbrella product: one customer-facing name and price over several stock
 * items, chosen by hand in the order they are preferred ("Dwarf Eureka Lemon"
 * = Eureka / Flying Dragon, Eureka / Trifoliata). Stock and picking keep
 * seeing the real stock items.
 */
export interface NurseryProductMember {
  product_id: number;
  stock_item_id: number;
  sort_order: number;
  active: boolean;
  sku_code: string | null;
  cultivar_name: string;
  cultivar_protection_status: string | null;
  /** The cultivar's crop ("Lemon"). A product is listed under its first member's. */
  crop_type_id?: number | null;
  crop_type_name?: string | null;
  rootstock_name: string | null;
  tree_type_id?: number;
  tree_type_code: string;
  tree_type_name: string;
  stock_type: 'B' | 'P';
}

export interface NurseryProduct {
  id: number;
  organisation_id: number;
  name: string;
  /** Chosen with the name; every member is this tree type. */
  tree_type_id: number | null;
  tree_type_code?: string | null;
  tree_type_name?: string | null;
  /** Sold on an order line, so its tree type is fixed. */
  has_orders?: boolean;
  sku_code: string | null;
  description: string | null;
  website_price: Numeric;
  /** The product's own barcode: what a retailer's purchase order names it by. */
  barcode?: string | null;
  tag_colour_name: string | null;
  tag_colour_hex: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  members: NurseryProductMember[];
}

export interface ProductPayload {
  name: string;
  tree_type_id: number;
  sku_code?: string | null;
  description?: string | null;
  website_price?: number | null;
  barcode?: string | null;
  tag_colour_name?: string | null;
  tag_colour_hex?: string | null;
  active?: boolean;
  /** Stock items it is filled from, most preferred first. */
  stock_item_ids: number[];
}

export interface NurseryBatch {
  id: number;
  organisation_id: number;
  batch_number: string;
  name: string;
  stock_type: 'B' | 'P';
  description: string | null;
  target_date: string | null;
  status: 'open' | 'closed';
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export type NurseryGrowingStatus =
  | 'planted' | 'growing' | 'budded' | 'graded'
  | 'allocated' | 'dug' | 'sold' | 'removed';

export interface NurseryGrowing {
  id: number;
  organisation_id: number;
  stock_item_id: number;
  batch_id: number;
  row_id: number;
  sequence_start: number;
  sequence_end: number | null;
  planted_date: string | null;
  budded_date: string | null;
  potted_date: string | null;
  ready_date: string | null;
  dig_date: string | null;
  sold_date: string | null;
  planted_count: number;
  beg_count: number | null;
  first_count: number | null;
  second_count: number | null;
  current_count: number;
  status: NurseryGrowingStatus;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  cultivar_id?: number;
  rootstock_id?: number;
  tree_type_id?: number;
  sku_code?: string;
  cultivar_name?: string;
  cultivar_protection_status?: string;
  rootstock_name?: string;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  batch_number?: string;
  batch_name?: string;
  row_number?: number;
  block_name?: string;
  farm_name?: string;
}

export interface NurserySourceRecord {
  id: number;
  organisation_id: number;
  growing_id: number;
  source_type: 'rootstock_planting' | 'graftwood' | 'budwood' | 'cutting' | 'seedling' | 'potting' | 'division' | 'purchase';
  source_growing_id: number | null;
  source_cultivar_id: number | null;
  source_supplier: string | null;
  source_batch_ref: string | null;
  quantity: number | null;
  source_date: string | null;
  notes: string | null;
  recorded_by_user_id: number | null;
  created_at: string;
  // Joined
  source_growing_status?: string;
  source_cultivar_name?: string;
}

export interface NurseryGrading {
  id: number;
  organisation_id: number;
  growing_id: number;
  grading_pass: number;
  grading_date: string;
  count_before: number;
  first_grade_count: number;
  second_grade_count: number;
  cull_count: number;
  graded_by_user_id: number | null;
  notes: string | null;
  created_at: string;
  graded_by_name?: string;
}

export type NurseryOrderStatus =
  | 'draft' | 'confirmed' | 'allocated' | 'partially_allocated'
  | 'picking' | 'picked' | 'shipped' | 'invoiced'
  | 'completed' | 'cancelled';

/** How the nursery sends an order (docs/nursery-despatch-plan.md). */
export type NurseryDespatchMethod = 'run' | 'carrier' | 'post' | 'collection' | 'cross_dock';

export const DESPATCH_METHOD_LABELS: Record<NurseryDespatchMethod, string> = {
  run: 'Our delivery run',
  carrier: 'Carrier',
  post: 'Post',
  collection: 'Customer collects',
  cross_dock: 'Cross dock',
};

export type NurseryOrderPriority = 'normal' | 'asap';

export interface NurseryOrder {
  id: number;
  nursery_org_id: number;
  customer_org_id: number | null;
  customer_id: number | null;
  order_number: string;
  order_date: string;
  requested_delivery_date: string | null;
  estimated_delivery_date: string | null;
  status: NurseryOrderStatus;
  placed_by_org: 'nursery' | 'customer';
  total_trees: number;
  total_value: NumericValue;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  /** What the customer chose at checkout; the delivery fee is priced on it. */
  fulfilment_method?: 'pickup' | 'delivery';
  delivery_postcode?: string | null;
  /** Snapshot of the address it goes to, taken when the address was chosen. */
  delivery_address?: string | null;
  delivery_fee?: NumericValue;
  payment_status?: 'not_required' | 'pending' | 'paid' | 'failed';
  paid_at?: string | null;
  despatch_method?: NurseryDespatchMethod;
  despatch_from?: string | null;
  despatch_by?: string | null;
  priority?: NurseryOrderPriority;
  collection_at?: string | null;
  delivery_address_id?: number | null;
  /** Doubles as the order's region when loads are planned. */
  delivery_zone_id?: number | null;
  /** Set on a backorder: the order it was split from. */
  parent_order_id?: number | null;
  /**
   * Through a cross dock: the route and delivery number the schedule gave the
   * store for its pick day (docs/retailer-po-and-cross-dock-plan.md).
   * xdock_schedule_id is null when the pick day was chosen by hand.
   */
  xdock_schedule_id?: number | null;
  xdock_route?: string | null;
  xdock_del_seq?: string | null;
  /** Set on an order made from a retailer's purchase order file. */
  po_import_id?: number | null;
  // Joined
  customer_name?: string;
  nursery_name?: string;
  delivery_zone_name?: string | null;
  parent_order_number?: string | null;
  load_id?: number | null;
  load_number?: string | null;
  load_despatch_date?: string | null;
  load_status?: NurseryLoadStatus | null;
  /** How its load is picked; 'free' means nothing is allocated, the picker chooses. */
  load_pick_mode?: PickMode | null;
  /** Paid online for trees that were never picked, at the price charged. */
  refund_due?: NumericValue | null;
  /** Set when the order was cancelled. A reason is optional. */
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  /** The customer's purchase order number. Printed on the docket and invoice. */
  customer_po?: string | null;
  lines?: NurseryOrderLine[];
  invoices?: NurseryInvoice[];
  /** Order page only: the orders a short despatch carried the rest of this one to. */
  backorders?: { id: number; order_number: string; status: NurseryOrderStatus; trees: number }[];
  /**
   * A bulk order waiting to be drawn down: off the despatch planner and off
   * every load, still holding its place in the line and its allocations.
   */
  held_for_drawdown?: boolean;
  /** Set on a draw-down: the bulk order its trees came off. */
  drawn_from_order_id?: number | null;
  drawn_from_order_number?: string | null;
  /** Order page only: the orders drawn down off this one. */
  drawdowns?: OrderDrawdown[];
  /** Order page only: who pays, and whether a PO is required. */
  account?: {
    payer_id: number;
    payer_name: string;
    parent_id: number | null;
    parent_name: string | null;
    po_required: boolean;
    terms: import('./nurseryAccounts').PaymentTerms | null;
    pay_before_despatch?: boolean;
    deposit_required?: boolean;
    deposit_pct?: number | null;
    /** Absent from a server older than customer freight. */
    freight?: import('./nurseryAccounts').FreightRule;
  } | null;
  /** Order page only: where the payer's account stands. */
  credit?: import('./nurseryAccounts').CreditPosition | null;
  /** Order page only: whether the account rules hold or refuse it. Absent from older servers. */
  account_hold?: import('./nurseryAccounts').OrderAccountHold | null;
  /** Order page only: the refund for a paid-online order that was short-picked. */
  refund?: import('./nurseryAccounts').OrderRefund | null;
  /** The customer is the nursery's walk-in customer: payment is taken at the counter. */
  customer_is_walk_in?: boolean;
  /** How it was paid at the counter. NULL for anything else, website orders included. */
  payment_method?: 'card' | 'cash' | null;
  /** What was taken at the counter, GST included. */
  counter_paid_amount?: NumericValue | null;
}

export interface NurseryOrderLine {
  id: number;
  order_id: number;
  stock_item_id: number;
  quantity_ordered: number;
  quantity_allocated: number;
  quantity_picked: number;
  quantity_shipped: number;
  unit_price: Numeric;
  discount_pct: NumericValue;
  line_total: Numeric;
  gst_amount: NumericValue;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** Set when the line was sold under an umbrella product. */
  product_id?: number | null;
  // Joined
  cultivar_name?: string;
  cultivar_protection_status?: string;
  rootstock_name?: string;
  tree_type_code?: string;
  tree_type_name?: string;
  product_name?: string | null;
  tag_colour_name?: string | null;
  tag_colour_hex?: string | null;
  /** For a product line: what its allocations actually drew on. */
  filled_from?: { cultivar_name: string; rootstock_name: string | null; quantity: number }[] | null;
  /** The lots it is allocated from. Absent from an older server. */
  allocations?: NurseryOrderLineAllocation[];
  /**
   * Still to allocate: trees other orders ahead of this line in the line for
   * stock want for the same thing (first come, first served). null when the
   * line is filled. Absent from an older server.
   */
  queue_ahead?: number | null;
}

/** One lot's trees held for an order line. */
export interface NurseryOrderLineAllocation {
  id: number;
  lot_id: number;
  lot_code: string | null;
  quantity: number;
  /** `allocated` until picked; only `allocated` can be released. */
  status: string;
  location_name: string | null;
  cultivar_name: string;
  rootstock_name: string | null;
}

export interface NurseryAllocation {
  id: number;
  organisation_id: number;
  order_line_id: number;
  lot_id: number;
  quantity: number;
  allocated_date: string;
  allocated_by_user_id: number | null;
  status: 'allocated' | 'picked' | 'shipped' | 'cancelled';
  created_at: string;
}

// ─── Loads and the despatch planner (docs/nursery-despatch-plan.md) ───

export type NurseryLoadType = 'run' | 'carrier' | 'post' | 'collection' | 'ad_hoc' | 'cross_dock';

export const LOAD_TYPE_LABELS: Record<NurseryLoadType, string> = {
  run: 'Delivery run',
  carrier: 'Carrier',
  post: 'Post',
  collection: 'Collections',
  ad_hoc: 'Local ad hoc',
  cross_dock: 'Cross dock',
};

export type NurseryLoadStatus = 'planning' | 'released' | 'picking' | 'loaded' | 'despatched' | 'cancelled';

/** Where an order stands against the planning window. */
export type DueClass = 'due' | 'overdue' | 'undated';

/** An order as the planner and a load see it. */
export interface DespatchOrder {
  id: number;
  order_number: string;
  status: NurseryOrderStatus;
  customer_id: number | null;
  customer_name: string | null;
  /** The retailer's store number, the customer's purchase order, and what the cross dock schedule said. */
  customer_code?: string | null;
  customer_po?: string | null;
  xdock_route?: string | null;
  xdock_del_seq?: string | null;
  despatch_method: NurseryDespatchMethod;
  priority: NurseryOrderPriority;
  despatch_from: string | null;
  despatch_by: string | null;
  requested_delivery_date: string | null;
  collection_at: string | null;
  delivery_zone_id: number | null;
  delivery_zone_name: string | null;
  delivery_address: string | null;
  delivery_postcode: string | null;
  parent_order_id: number | null;
  total_trees: number;
  allocated_trees: number;
  /** Lines with no price. A load with one picked is refused at despatch. */
  unpriced_lines?: number;
  /** The farms its allocated stock sits at; a null farm_id is a lot with no location. */
  stock_farms: { farm_id: number | null; farm_name: string | null; quantity: number }[];
  load_id: number | null;
  stop_sequence: number | null;
  load_number: string | null;
  load_despatch_date: string | null;
  load_status: NurseryLoadStatus | null;
  load_type: NurseryLoadType | null;
  /** The delivery address's coordinates, when it was picked from Places. */
  delivery_lat: NumericValue | null;
  delivery_lng: NumericValue | null;
  scheduled_notice_date: string | null;
  despatched_notice_at: string | null;
  // Planner only
  due_class?: DueClass;
  fully_allocated?: boolean;
}

export interface LoadCheck {
  code: 'UNALLOCATED' | 'NEEDS_TRANSFER' | 'STOCK_FARM_UNKNOWN' | 'NO_PICKER' | 'OVER_CAPACITY' | 'OUTSIDE_ZONES' | 'EMPTY'
    | 'STOCK_SHORT' | 'UNPRICED';
  severity: 'warning' | 'info';
  message: string;
  order_ids?: number[];
}

/**
 * How a load is picked: from allocations made ahead ('allocated'), or by the
 * picker choosing lots as they go, nothing allocated ('free').
 */
export type PickMode = 'allocated' | 'free';

export const PICK_MODE_LABELS: Record<PickMode, string> = {
  allocated: 'Allocate first',
  free: 'Pickers choose the lots',
};

export interface DespatchSettings {
  default_pick_mode: PickMode;
  /** Days after a load's date its unpicked orders are backordered; null is off. */
  missed_backorder_days?: number | null;
  updated_at: string | null;
  is_default: boolean;
}

export interface NurseryLoad {
  /** The organisation's missed_backorder_days, on a load not yet gone. */
  missed_backorder_days?: number | null;
  id: number;
  organisation_id: number;
  load_number: string;
  load_type: NurseryLoadType;
  /** Absent from an older server: read it as 'allocated'. */
  pick_mode?: PickMode;
  is_daily: boolean;
  despatch_farm_id: number;
  despatch_farm_name: string;
  despatch_date: string;
  status: NurseryLoadStatus;
  zone_ids: number[];
  zone_names: string[];
  vehicle: string | null;
  driver_name: string | null;
  carrier_name: string | null;
  consignment_ref: string | null;
  capacity_trees: number | null;
  notes: string | null;
  released_at: string | null;
  despatched_at: string | null;
  created_at: string;
  updated_at: string;
  total_trees: number;
  // List only
  order_count?: number;
  // Detail only
  picker_count?: number;
  despatch_farm_point?: { lat: number; lng: number } | null;
  orders?: DespatchOrder[];
  checks?: LoadCheck[];
  /** How it is picked can still change: no tree picked yet. Absent from an older server. */
  can_change_pick_mode?: boolean;
  /** After a pick-mode change to 'free': trees its orders gave back. */
  released_trees?: number;
}

export interface CreateLoadPayload {
  load_type: NurseryLoadType;
  despatch_farm_id: number;
  despatch_date: string;
  zone_ids?: number[];
  vehicle?: string | null;
  driver_name?: string | null;
  carrier_name?: string | null;
  consignment_ref?: string | null;
  capacity_trees?: number | null;
  notes?: string | null;
  order_ids?: number[];
  /** Leave out to take the organisation's default. */
  pick_mode?: PickMode;
}

export type UpdateLoadPayload = Partial<Omit<CreateLoadPayload, 'load_type' | 'order_ids' | 'pick_mode'>>;

// ─── Picking (docs/nursery-despatch-plan.md, Phase 2) ───

export type PickShortReason = 'not_found' | 'count_low' | 'not_ready' | 'damaged' | 'other';

export const SHORT_REASON_LABELS: Record<PickShortReason, string> = {
  not_found: 'Could not find them',
  count_low: 'Fewer there than recorded',
  not_ready: 'Not ready yet',
  damaged: 'Damaged or not saleable',
  other: 'Something else',
};

/** A load a picker can work. No prices anywhere in picking. */
export interface PickLoad {
  id: number;
  load_number: string;
  load_type: NurseryLoadType;
  is_daily: boolean;
  despatch_date: string;
  status: NurseryLoadStatus;
  pick_mode?: PickMode;
  despatch_farm_id: number;
  despatch_farm_name: string;
  order_count: number;
  /** Allocations to pick, or on a free load order lines. */
  item_count: number;
  picked_count: number;
  total_trees: number;
  first_collection_at: string | null;
}

export interface PickLoadsResponse {
  loads: PickLoad[];
  /** How many farms a picker-only user is assigned; null for everyone else. */
  farms_assigned: number | null;
}

/** One allocation to pick. */
export interface PickItem {
  allocation_id: number;
  quantity: number;
  allocation_status: string;
  substituted_from_allocation_id: number | null;
  order_line_id: number;
  order_id: number;
  order_number: string;
  collection_at: string | null;
  despatch_method: NurseryDespatchMethod;
  customer_name: string | null;
  /** Who the order is for; a load is picked one customer at a time (pickCustomers.ts). */
  customer_id?: number | null;
  customer_org_id?: number | null;
  customer_code?: string | null;
  /** The server's key for the customer: 'c<id>', 'o<org id>' or 'order<id>'. Claims use it. */
  customer_key?: string;
  stop_sequence: number | null;
  lot_id: number;
  lot_code: string | null;
  lot_count: number;
  lot_ready_date: string | null;
  lot_stage: string;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  /** The order line's notes: a sticker to apply, a comment from the purchase order. */
  line_notes?: string | null;
  /** The umbrella product the line was sold as, when it was. */
  sold_as?: string | null;
  tag_colour_name?: string | null;
  tag_colour_hex?: string | null;
  location_name: string | null;
  block_name: string | null;
  /** The block's place in the farm's walk; null when none is set. */
  block_sort_order?: number | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  farm_id: number | null;
  farm_name: string | null;
  at_despatch_farm: boolean;
  pick: {
    quantity_picked: number;
    status: 'picked' | 'short';
    short_reason: PickShortReason | null;
    notes: string | null;
    picked_at: string;
    picked_by_name: string | null;
  } | null;
}

/** A lot a line on a free load can be taken from, or has been. */
export interface FreePickLot {
  lot_id: number;
  lot_code: string | null;
  lot_count: number;
  lot_ready_date: string | null;
  lot_stage: string;
  cultivar_name: string;
  rootstock_name: string | null;
  location_name: string | null;
  block_name: string | null;
  block_sort_order: number | null;
  row_number: number | null;
  row_side: RowSide | null;
  sequence_number: number | null;
  /** Trees nobody else holds: the lot's count less other orders' allocations. */
  free: number;
  pick: {
    allocation_id: number;
    quantity_picked: number;
    picked_at: string;
    notes: string | null;
    picked_by_name: string | null;
  } | null;
}

/** One order line on a load picked without allocating. */
export interface FreePickLine {
  order_line_id: number;
  quantity_ordered: number;
  quantity_picked: number;
  remaining: number;
  /** picked: all of it; short: closed with a reason; open: still being picked. */
  status: 'open' | 'picked' | 'short';
  resolved: boolean;
  line_notes: string | null;
  pick_short_reason: PickShortReason | null;
  pick_short_notes: string | null;
  pick_short_at: string | null;
  short_by_name: string | null;
  order_id: number;
  order_number: string;
  collection_at: string | null;
  despatch_method: NurseryDespatchMethod;
  customer_id: number | null;
  customer_org_id: number | null;
  customer_key: string;
  customer_name: string | null;
  customer_code: string | null;
  stop_sequence: number | null;
  xdock_route?: string | null;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  sold_as: string | null;
  tag_colour_name: string | null;
  tag_colour_hex: string | null;
  /** Free, ready stock of this line's trees at other farms (has to be moved first). */
  free_elsewhere: number;
  /** Lots at the despatch farm in walk order, and any already picked from. */
  lots: FreePickLot[];
}

export interface PickList {
  /** Absent from an older server: read it as 'allocated'. */
  mode?: PickMode;
  load: Pick<NurseryLoad, 'id' | 'load_number' | 'load_type' | 'is_daily' | 'despatch_date' | 'status'
    | 'despatch_farm_id' | 'despatch_farm_name' | 'vehicle' | 'driver_name' | 'notes' | 'pick_mode'>;
  items: PickItem[];
  /** On a free load: the lines, in walk order. */
  lines?: FreePickLine[];
  /** Who is picking for which customer right now. Absent from an older server. */
  claims?: PickClaim[];
  /** Orders going under a quarantine protocol, and where to take them once picked. Absent from an older server. */
  quarantine?: PickQuarantineOrder[];
  progress: { items: number; resolved: number; trees: number; picked_trees: number };
}

/** One picker's hold on a customer on a load; another picker cannot pick for them. */
export interface PickClaim {
  customer_key: string;
  user_id: number;
  user_name: string | null;
  claimed_at: string;
  last_active_at: string;
  mine: boolean;
}

export interface PickAlternative {
  lot_id: number;
  lot_code: string | null;
  current_count: number;
  ready_date: string | null;
  /** What the lot is — an umbrella product's lots are not all the same tree. */
  cultivar_name?: string;
  rootstock_name?: string | null;
  location_name: string | null;
  block_name: string | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  available: number;
}

// ─── Despatch, transfers and refills (docs/nursery-despatch-plan.md, Phase 3) ───

/** What one order on a load was short, for the despatch dialog. */
export interface LoadShortfall {
  order_id: number;
  order_number: string;
  customer_name: string | null;
  despatch_from: string | null;
  despatch_by: string | null;
  paid_online: boolean;
  /** Nothing picked: the whole order is backordered (Rob, 2026-10-15). */
  nothing_picked?: boolean;
  lines: {
    line_id: number;
    cultivar_name: string;
    rootstock_name: string | null;
    tree_type_name: string;
    ordered: number;
    picked: number;
    short: number;
  }[];
}

export interface DespatchChoice {
  order_id: number;
  action: 'backorder' | 'cancel';
  despatch_from?: string | null;
  despatch_by?: string | null;
}

export interface DespatchResult {
  load_id: number;
  load_number: string;
  movements: number;
  backorders: { order_id: number; order_number: string; parent_order_number: string; trees: number }[];
  cancelled: string[];
  /** Invoices made final by the despatch. */
  invoices_authorised?: number;
  /** Left as drafts because a line has no price: billing it at $0 is not despatch's call. */
  /** Drafts left unauthorised: lines with no price, or an outside carrier's freight not typed yet. */
  invoices_held?: { invoice_id: number; invoice_number: string; order_number: string; lines: string[]; freight_missing?: boolean }[];
  /** Refunds issued for paid-online orders that were short. */
  refunds?: { order_id: number; amount: number; status: 'pending' | 'succeeded' | 'failed'; error: string | null }[];
  /** Lots whose count was lower than what was picked from them: set to 0, never a reason to stop the load. */
  lots_short?: { lot_id: number; lot_code: string | null; showed: number; picked: number; order_number: string }[];
  /** Orders taken off the load with nothing picked: a backend from before 2026-10-15 only; now always empty. */
  taken_off?: { order_id: number; order_number: string; customer_name: string | null }[];
}

export type TransferStatus = 'planned' | 'in_transit' | 'received' | 'cancelled';

export interface NurseryTransferLine {
  id: number;
  transfer_id: number;
  lot_id: number;
  quantity: number;
  allocation_id: number | null;
  received_quantity: number | null;
  to_lot_id: number | null;
  notes: string | null;
  lot_code: string | null;
  lot_count: number;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  location_name: string | null;
  block_name: string | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  order_number: string | null;
  customer_name: string | null;
  to_lot_code: string | null;
}

export interface NurseryTransfer {
  id: number;
  transfer_number: string;
  from_farm_id: number;
  from_farm_name: string;
  to_farm_id: number;
  to_farm_name: string;
  to_location_id: number;
  to_location_name: string;
  scheduled_date: string;
  status: TransferStatus;
  source: 'manual' | 'load' | 'refill';
  load_id: number | null;
  load_number: string | null;
  refill_rule_id: number | null;
  vehicle: string | null;
  driver_name: string | null;
  notes: string | null;
  sent_at: string | null;
  received_at: string | null;
  total_trees: number;
  // List only
  line_count?: number;
  received_trees?: number;
  // Detail only
  lines?: NurseryTransferLine[];
}

export interface CreateTransferPayload {
  from_farm_id: number;
  to_farm_id: number;
  to_location_id: number;
  scheduled_date: string;
  vehicle?: string | null;
  driver_name?: string | null;
  notes?: string | null;
  lines: { lot_id: number; quantity: number; allocation_id?: number | null }[];
}

// ─── Despatchable places and auto-allocation (2026-10-07) ───

/**
 * A farm, block, row (one side or both) or single location whose ready lots
 * orders are allocated from by themselves, oldest ready first.
 */
export interface DespatchPlace {
  id: number;
  farm_id: number;
  farm_name: string;
  block_id: number | null;
  block_name: string | null;
  row_number: number | null;
  /** With a row: null is both sides. */
  row_side: RowSide | null;
  location_id: number | null;
  location_name: string | null;
  location_type: string | null;
  /** Active lots standing there, and ready trees not allocated. */
  lots: number;
  ready_free: number;
}

export interface DespatchPlacePayload {
  farm_id: number;
  block_id?: number | null;
  row_number?: number | null;
  row_side?: RowSide | null;
  location_id?: number | null;
}

export interface AutoAllocateOrderResult {
  order_id: number;
  order_number: string;
  allocated: { order_line_id: number; lot_id: number; lot_code: string | null; location_name: string | null; quantity: number }[];
  /** Lines still short after it, by how many. */
  short: { order_line_id: number; quantity: number }[];
  /** Why it was not tried (an order past allocation). */
  skipped?: string;
}

export interface AutoAllocateResult {
  orders: AutoAllocateOrderResult[];
  trees: number;
  /** Nothing is despatchable yet, so nothing was tried. */
  no_places: boolean;
}

// ─── Customer notices (Phase 4) ───

export type NoticeKind = 'scheduled' | 'despatched';

/**
 * The supplier manifest Bunnings' greenlife cross dock wants with a truck:
 * one row per purchase order. Trees come from the picks once picking has
 * started (`items_from`), else from the order; the packing is typed here.
 */
export interface SupplierManifestHeader {
  supplier_code?: string | null;
  batch_number?: string | null;
  phac_numbers?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  total_stacks?: number | null;
  total_pallets?: number | null;
}

export interface SupplierManifestOrder {
  order_id: number;
  order_number: string;
  invoice_number?: string | null;
  /** D and the invoice number, as the old system numbered dockets. Null until the order is invoiced. */
  docket_number?: string | null;
  customer_po: string | null;
  customer_name: string | null;
  customer_code: string | null;
  /** The store's state (code), which manifest it is on; null when the address has none. */
  state?: string | null;
  /** The store's name in Bunnings' own list, as the sheet will show it. */
  store_name?: string | null;
  xdock_route: string | null;
  xdock_del_seq: string | null;
  ordered_trees: number;
  picked_trees: number;
  items: number;
  items_from: 'picked' | 'ordered';
  trays: number | null;
  stillages: number | null;
  seedling_racks: number | null;
  /** Rides on that order's stillage: no stillage count of its own. */
  shares_with_order_id?: number | null;
  /** The cross dock it goes through, and why: its customer's, its head office's, or its state's. */
  cross_dock_id?: number | null;
  cross_dock_name?: string | null;
  cross_dock_via?: 'customer' | 'head_office' | 'state' | null;
  /** The key of the manifest it is on. Missing from a server older than 2026-10-15. */
  group?: string;
}

/**
 * One manifest on a load: one per cross dock (Rob, 2026-10-15), with its own
 * stacks, pallets and PHAC numbers. Stores that go through no cross dock get
 * one per state, which cannot be emailed. A server from 2026-10-14 sent one
 * per state with no `key` or `cross_dock`.
 */
export interface SupplierManifestState {
  /** `dock-<id>`, else the state's code or 'none'. */
  key?: string;
  /** Its stores' state when they share one; null for several, or an address with none. */
  state: string | null;
  /** Every state its stores are in. */
  states?: string[];
  label: string;
  cross_dock?: { id: number; name: string; address: string | null; recipients: string[] } | null;
  order_ids: number[];
  total_stacks: number | null;
  total_pallets: number | null;
  phac_numbers: string | null;
}

export interface SupplierManifestEmail {
  recipients: string[];
  sent_ok: boolean;
  error: string | null;
  sent_at: string;
  sent_by: string | null;
  /** The cross dock it went to; null for one email with every manifest. */
  cross_dock_name?: string | null;
}

/**
 * A cross dock a nursery sends cross dock loads to: the address printed on
 * its manifest, the states that go there unless a customer says otherwise,
 * and who its manifest is emailed to.
 */
export interface NurseryCrossDock {
  id: number;
  name: string;
  address: string | null;
  states: string[];
  recipients: string[];
  active: boolean;
  /** Customers (stores or head offices) sent here by name. */
  customer_count: number;
  updated_at: string;
}

export interface NurseryCrossDockInput {
  name: string;
  address: string | null;
  states: string[];
  recipients: string[];
}

export interface SupplierManifest {
  load: { id: number; load_number: string; load_type: NurseryLoadType; status: NurseryLoadStatus; despatch_date: string };
  supplier: { name: string; address: string };
  header: SupplierManifestHeader;
  /** The head office whose account number filled the supplier code, when the stores share one. */
  supplier_code_from?: { head_office: string; code: string } | null;
  /** False until someone saves this load's manifest; the header is then a starting point. */
  saved: boolean;
  /** A copy of every manifest goes to these (before 2026-10-15: everyone it went to). */
  recipients?: string[];
  /** The organisation's cross docks. Missing from a server older than 2026-10-15. */
  cross_docks?: { id: number; name: string; states: string[]; recipients: string[] }[];
  /** The latest sends of this load's manifest, newest first. */
  emails?: SupplierManifestEmail[];
  orders: SupplierManifestOrder[];
  /** One per manifest on the load (per cross dock). Missing from a server older than 2026-10-14. */
  states?: SupplierManifestState[];
}

export interface NoticePreview {
  kind: NoticeKind;
  load: { id: number; load_number: string; despatch_date: string; status: NurseryLoadStatus };
  nursery_name: string;
  contact_email: string | null;
  orders: {
    order_id: number;
    order_number: string;
    customer_name: string | null;
    email: string | null;
    already_told: boolean;
    told_for_date: string | null;
    told_at: string | null;
    subject: string;
    text: string;
  }[];
}

export interface NoticeResult {
  kind: NoticeKind;
  results: { order_id: number; order_number: string; outcome: 'sent' | 'no_email' | 'already_told' | 'failed'; error?: string; email?: string }[];
}

/** One line of a backorder, with what is free for it now. GET /nursery/backorders. */
export interface BackorderLine {
  id: number;
  item_name: string | null;
  item_kind: 'plant' | 'merchandise' | null;
  tree_type_name: string | null;
  quantity_ordered: number;
  quantity_allocated: number;
  short: number;
  /** Ready trees nobody has allocated that could fill this line. */
  free_now: number;
  /** Of `short`, what free stock covers, shared out first in line. An estimate; Allocate settles it. */
  can_fill_now: number;
}

/** Where a cross dock backorder goes next: the store's next pick day and any load already made for it. */
export interface BackorderCrossDock {
  ok: boolean;
  message?: string;
  date?: string;
  day_name?: string;
  route?: string | null;
  del_seq?: string | null;
  farm_id?: number | null;
  load?: { id: number; load_number: string; status: NurseryLoadStatus; addable: boolean } | null;
}

export interface BackorderOrder {
  id: number;
  order_number: string;
  status: NurseryOrderStatus;
  customer_id: number | null;
  customer_name: string | null;
  customer_code: string | null;
  customer_po: string | null;
  despatch_method: NurseryDespatchMethod;
  despatch_from: string | null;
  despatch_by: string | null;
  delivery_zone_id: number | null;
  delivery_zone_name: string | null;
  parent_order_id: number;
  parent_order_number: string;
  despatch_farm_id: number | null;
  total_trees: number;
  allocated_trees: number;
  short_trees: number;
  can_fill_now: number;
  /** allocated: nothing short; can_fill: free stock covers it; part: some; waiting: none free. */
  ready: 'allocated' | 'can_fill' | 'part' | 'waiting';
  load_id: number | null;
  load_number: string | null;
  load_status: NurseryLoadStatus | null;
  load_despatch_date: string | null;
  cross_dock: BackorderCrossDock | null;
  lines: BackorderLine[];
}

export interface BackorderList {
  orders: BackorderOrder[];
  /** Loads orders can still join. */
  open_loads: NurseryLoad[];
  farms: { id: number; name: string; picker_count: number }[];
  summary: { orders: number; short_trees: number; can_fill_now: number; not_on_load: number; ready_to_go: number };
}

/** One order in a backorder chain. GET /nursery/orders/:id/shipments. */
export interface ShipmentFamilyOrder {
  id: number;
  order_number: string;
  status: NurseryOrderStatus;
  parent_order_id: number | null;
  parent_order_number: string | null;
  /** 0 is the original order, 1 its backorder, 2 that one's backorder… */
  depth: number;
  created_at: string;
  despatch_method: NurseryDespatchMethod | null;
  despatch_from: string | null;
  despatch_by: string | null;
  cancelled_at: string | null;
  load_id: number | null;
  load_number: string | null;
  load_status: NurseryLoadStatus | null;
  load_despatch_date: string | null;
  despatched_at: string | null;
  carrier_name: string | null;
  consignment_ref: string | null;
  trees_ordered: number;
  trees_shipped: number;
  /** shipped: went with trees on it; nothing_sent: went with none, all of it backordered; open: still to go. */
  outcome: 'shipped' | 'nothing_sent' | 'cancelled' | 'open';
  backorder_ids: number[];
  lines: { key: string; item_name: string | null; tree_type_name: string | null; quantity_ordered: number; quantity_shipped: number }[];
}

/** One tree across the chain: what was wanted, what went on which order, what is still to go. */
export interface ShipmentItem {
  key: string;
  item_name: string | null;
  tree_type_name: string | null;
  /** What the original order wanted. */
  ordered: number;
  shipped: number;
  waiting: number;
  /** Will never go: cancelled at despatch, or on a cancelled backorder. */
  not_sent: number;
  shipped_in: { order_id: number; quantity: number }[];
  waiting_in: { order_id: number; quantity: number }[];
}

export interface OrderShipments {
  root_id: number;
  root_order_number: string;
  orders: ShipmentFamilyOrder[];
  /** The orders that went with trees on them, in the order they left. */
  shipment_order_ids: number[];
  items: ShipmentItem[];
  totals: { ordered: number; shipped: number; waiting: number; not_sent: number };
}

export interface BackorderCrossDockResult {
  order_id: number;
  order_number?: string;
  placed: boolean;
  date?: string;
  load_id?: number;
  load_number?: string;
  message?: string;
}

export interface DespatchPlanner {
  from: string;
  to: string;
  orders: DespatchOrder[];
  summary: {
    orders: number;
    trees: number;
    on_load: number;
    not_on_load: number;
    not_on_load_trees: number;
    overdue_not_on_load: number;
    undated: number;
    on_load_unallocated: number;
    asap_not_on_load: number;
  };
  farms: { id: number; name: string; picker_count: number }[];
  /** Loads not yet despatched or cancelled: the ones orders can be added to. */
  open_loads: NurseryLoad[];
}

export interface NurseryInvoice {
  id: number;
  nursery_org_id: number;
  customer_org_id: number | null;
  customer_id: number | null;
  order_id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  subtotal: NumericValue;
  gst_total: NumericValue;
  total_amount: NumericValue;
  /** 'sent' means authorised; 'cancelled' means void. Overdue is derived, never stored. */
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  authorised_at?: string | null;
  amount_paid?: NumericValue;
  amount_credited?: NumericValue;
  xero_sync_status?: 'pending' | 'pushing' | 'synced' | 'failed' | null;
  // Joined
  customer_name?: string;
  order_number?: string;
}

/** A ready lot matching an order line's SKU, net of existing allocations. */
export interface AvailableStock {
  id: number;
  lot_code: string;
  current_count: number;
  current_stage: string;
  ready_date: string | null;
  location_name: string | null;
  location_type: string | null;
  available_count: number;
  cultivar_name?: string;
  rootstock_name?: string | null;
}

// ─── Payload types ───

export interface CreateRowPayload {
  block_id: number;
  row_number: number;
  row_length_meters?: number;
  row_spacing_meters?: number;
  capacity?: number;
  notes?: string;
}

export interface BulkCreateRowsPayload {
  block_id: number;
  start_row: number;
  end_row: number;
  row_length_meters?: number;
  row_spacing_meters?: number;
  capacity?: number;
}

export interface CreateStockItemPayload {
  kind?: StockItemKind;
  name?: string;
  unit?: string;
  category_id?: number | null;
  cultivar_id?: number;
  rootstock_id?: number;
  tree_type_id?: number;
  sku_code?: string;
  description?: string;
  website_price?: number | null;
  barcode?: string | null;
  /** Omitted: follows the tree type's purpose (propagation stock is not offered). */
  sellable?: boolean;
}

/** The shop's own grouping of stock items (Fruit trees, Natives, Potting mix…). */
export interface RetailCategory {
  id: number;
  organisation_id: number;
  name: string;
  sort_order: number;
  active: boolean;
  /** Active stock items in it. */
  item_count?: number;
}

/** A supplier's tag barcode for a stock item, beside the item's own. */
export interface StockItemBarcode {
  id?: number;
  barcode: string;
  supplier_id: number | null;
  supplier_name?: string | null;
}

export interface WebsiteSignup {
  id: number;
  user_id: number;
  organisation_id: number;
  email: string;
  customer_number: string | null;
  status: 'pending' | 'linked' | 'dismissed';
  linked_customer_id: number | null;
  linked_by_user_id: number | null;
  linked_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  first_name?: string | null;
  last_name?: string | null;
  user_email?: string;
  organisation_name?: string | null;
  linked_customer_name?: string | null;
}

export interface LinkWebsiteSignupPayload {
  customer_id?: number;
  customer_name?: string;
  customer_tier_id?: number | null;
}

export interface CreateBatchPayload {
  batch_number: string;
  name: string;
  stock_type: 'B' | 'P';
  description?: string;
  target_date?: string;
}

export interface CreateGrowingPayload {
  stock_item_id: number;
  batch_id: number;
  row_id: number;
  sequence_start?: number;
  sequence_end?: number;
  planted_date?: string;
  potted_date?: string;
  planted_count: number;
  notes?: string;
}

export interface CreateSourceRecordPayload {
  source_type: NurserySourceRecord['source_type'];
  source_growing_id?: number;
  source_cultivar_id?: number;
  source_supplier?: string;
  source_batch_ref?: string;
  quantity?: number;
  source_date?: string;
  notes?: string;
}

export interface RecordGradingPayload {
  grading_pass: number;
  grading_date: string;
  count_before: number;
  first_grade_count?: number;
  second_grade_count?: number;
  cull_count?: number;
  notes?: string;
}

export interface CreateOrderPayload {
  customer_id?: number;
  customer_org_id?: number;
  order_date: string;
  requested_delivery_date?: string;
  estimated_delivery_date?: string;
  placed_by_org?: 'nursery' | 'customer';
  notes?: string;
  customer_po?: string | null;
  lines?: OrderLinePayload[];
  despatch_method?: NurseryDespatchMethod;
  despatch_from?: string | null;
  despatch_by?: string | null;
  priority?: NurseryOrderPriority;
  collection_at?: string | null;
  delivery_address_id?: number | null;
  delivery_zone_id?: number | null;
}

/** The despatch fields an order edit may carry. */
/**
 * One row of the order sheet: a stock item or an umbrella product, the ready
 * stock behind it, and what the order being entered already holds of it.
 */
export interface OrderSheetRow {
  /** "s:<stock item id>" or "p:<product id>". */
  key: string;
  stock_item_id: number | null;
  product_id: number | null;
  name: string;
  /**
   * 'merchandise' for potting mix, pots…: one "Merchandise" section after the
   * tree types, grouped by shop category in place of crop. Absent from an older server.
   */
  item_kind?: StockItemKind;
  /** What one is (bag, litre). Merchandise only. */
  unit?: string | null;
  rootstock_name: string | null;
  sku_code: string | null;
  tag_colour_name: string | null;
  tag_colour_hex: string | null;
  crop_type_name: string | null;
  tree_type_id: number;
  tree_type_code: string;
  tree_type_name: string;
  tree_type_sort: number;
  stock_type: 'B' | 'P';
  /** Ready trees, at the chosen farm or all of them. */
  ready: number;
  /** Ready trees not allocated to an order. */
  free: number;
  /** Trees other confirmed orders want and have not been allocated. */
  wanted: number;
  /**
   * The part of `wanted` that is ahead of this order in the line for stock
   * (first come, first served): all of it for an order not confirmed yet.
   * Absent from an older server.
   */
  wanted_ahead?: number;
  /**
   * The customer's price-list prices for it, lowest break first; [] when
   * their list does not price it. Only when the sheet was asked for a
   * customer. Before tier discount.
   */
  prices?: PriceBreak[];
  quantity: number;
  /** Trees already allocated to it. Absent from an older server. */
  allocated?: number;
  unit_price: number | null;
  discount_pct: number;
  line_total: number;
  /** More than 1 means the order has several lines for this row; change it on the order page. */
  line_count: number;
  /** Its cultivar or rootstock has a description or talking points to read. Absent from an older server. */
  has_info?: boolean;
  /** Its own barcode, from the stock item or product. Absent from an older server. */
  barcode?: string | null;
  /** Retailers' barcodes matched to it on a purchase order import. Absent from an older server. */
  retailer_barcodes?: string[];
  /** A stock item: the active products it is sold under. Absent from an older server. */
  in_products?: number[];
}

/** One cultivar of something the nursery sells, as staff read about it. */
export interface NurseryCultivarInfo {
  cultivar_id: number;
  name: string;
  trade_name: string | null;
  crop_type_name: string | null;
  protection_status: string | null;
  role: 'variety' | 'rootstock';
  description: string | null;
  sales_profile: CultivarSalesProfile | null;
}

/** What to read about: a stock item, or an umbrella product and all its members. */
export type CultivarInfoSubject = { stock_item_id: number } | { product_id: number };

export interface OrderSheetOrder {
  id: number;
  order_number: string;
  status: NurseryOrderStatus;
  total_trees: number;
  total_value: number;
  /** Quantities can be changed on the sheet: any order not yet being picked. */
  editable: boolean;
}

/** One volume break on a price list: this price from this many up. */
export interface PriceBreak {
  min_quantity: number;
  unit_price: number;
}

/** Whose prices the sheet's rows carry, and from which list. */
export interface OrderSheetPricing {
  customer_id: number;
  price_list_id: number | null;
  price_list_name: string | null;
  source_label: string | null;
  /** False when the customer's list is not in force today: nothing is priced. */
  in_force: boolean;
  /** The customer's tier discount, taken off every price on a line. */
  discount_pct: number;
}

export interface OrderSheet {
  farms: { id: number; name: string }[];
  rows: OrderSheetRow[];
  order: OrderSheetOrder | null;
  /** Present when the sheet was asked for a customer (or has an order). Absent from an older server. */
  pricing?: OrderSheetPricing | null;
}

/** What saving one cell returns: the line as it now stands, and the order's totals. */
export interface OrderSheetLineResult {
  line: { id: number; quantity: number; allocated?: number; unit_price: number | null; discount_pct: number; line_total: number } | null;
  order: { id: number; total_trees: number; total_value: number; status?: NurseryOrderStatus };
  /** Allocated trees a lowered quantity sent back to free stock. */
  released?: number;
}

export type OrderDespatchPayload = Pick<CreateOrderPayload,
  'despatch_method' | 'despatch_from' | 'despatch_by' | 'priority' |
  'collection_at' | 'delivery_address_id' | 'delivery_zone_id'>;

/** A holder of the nursery Order Picker seat and the farms they pick at. */
export interface NurseryPicker {
  user_id: number;
  name: string;
  email: string | null;
  farm_ids: number[];
}

export interface NurseryPickersResponse {
  pickers: NurseryPicker[];
  farms: { id: number; name: string }[];
}

/** A line names a stock item or an umbrella product — exactly one. */
export interface OrderLinePayload {
  stock_item_id?: number;
  product_id?: number;
  quantity_ordered: number;
  unit_price?: number;
  discount_pct?: number;
  notes?: string;
}

export interface AllocatePayload {
  order_line_id: number;
  lot_id: number;
  quantity: number;
  allocated_date?: string;
}

export interface CreateInvoicePayload {
  order_id: number;
  invoice_date: string;
  due_date?: string;
  notes?: string;
  /** An outside carrier's price, when the order goes by carrier. */
  freight_amount?: number | null;
  /** Freight for a customer with none set, chosen on the form. */
  freight_choice?: import('./nurseryAccounts').FreightChoice | null;
}

export interface CreateTreeTypePayload {
  code: string;
  name: string;
  stock_type: 'B' | 'P';
  description?: string;
  sort_order?: number;
  purpose?: TreeTypePurpose;
}

// ─── Customer tiers ───

export interface NurseryCustomerTier {
  id: number;
  organisation_id: number;
  name: string;
  code: string | null;
  default_discount_pct: NumericValue;
  sort_order: number;
  is_website_default: boolean;
  requires_approval: boolean;
  payment_mode: 'online' | 'on_account';
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomerTierPayload {
  name: string;
  code?: string;
  default_discount_pct?: number;
  sort_order?: number;
  is_website_default?: boolean;
  requires_approval?: boolean;
  payment_mode?: 'online' | 'on_account';
}

// ─── Trade-account requests (wholesale applications) ───

export interface TradeAccountRequest {
  id: number;
  organisation_id: number;
  customer_org_id: number;
  user_id: number | null;
  requested_tier_id: number | null;
  business_name: string;
  abn: string | null;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  delivery_address: string | null;
  notes: string | null;
  status: 'pending' | 'approved' | 'declined';
  review_notes: string | null;
  granted_tier_id: number | null;
  linked_customer_id: number | null;
  reviewed_by_user_id: number | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined
  requested_tier_name?: string | null;
  granted_tier_name?: string | null;
  customer_org_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  user_email?: string | null;
}

export interface ReviewTradeAccountPayload {
  granted_tier_id?: number | null;
  review_notes?: string;
}

// ─── Delivery zones ───

export interface NurseryDeliveryZone {
  id: number;
  organisation_id: number;
  customer_tier_id: number | null;
  name: string;
  match_type: 'all' | 'postcode_prefix' | 'postcode_list';
  match_value: string | null;
  flat_fee: NumericValue;
  /** Percentage of the trees' value (price x quantity, ex GST), added to the flat fee. */
  fee_pct?: NumericValue;
  free_over_subtotal: Numeric;
  active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Joined
  tier_name?: string | null;
}

export interface CreateDeliveryZonePayload {
  customer_tier_id?: number | null;
  name: string;
  match_type?: 'all' | 'postcode_prefix' | 'postcode_list';
  match_value?: string | null;
  flat_fee?: number;
  fee_pct?: number;
  free_over_subtotal?: number | null;
  sort_order?: number;
  active?: boolean;
}

// ─── Customers (hybrid) ───

export type NurseryCustomerAddressType = 'billing' | 'delivery';

export interface NurseryCustomerAddress {
  id?: number;
  address_type: NurseryCustomerAddressType;
  label?: string | null;
  line1?: string | null;
  line2?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  /** Google Maps plus code, full (4RJ7+2V...) or short with a locality. */
  plus_code?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  instructions?: string | null;
  is_default?: boolean;
  /** From Places when the address was picked; used to order delivery stops. */
  lat?: number | null;
  lng?: number | null;
}

/** One of a customer's people. The main one is also the customer's own contact. */
export interface NurseryCustomerContact {
  id: number;
  customer_id: number;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  position: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  after_hours: string | null;
  is_main: boolean;
  source: 'manual' | 'legacy';
}

export interface NurseryCustomer {
  id: number;
  organisation_id: number;
  linked_org_id: number | null;
  customer_tier_id: number | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  abn: string | null;
  billing_address: string | null;
  delivery_address: string | null;
  billing_same_as_delivery?: boolean;
  addresses?: NurseryCustomerAddress[];
  notes: string | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  /** The account this customer is a site of (its head office). Two levels only. */
  parent_customer_id?: number | null;
  /** A site's invoices are paid by its head office unless this is false. */
  bills_to_parent?: boolean;
  /** The retailer's own number for the store or account. */
  customer_code?: string | null;
  /** NULL on each of these means: inherit from the head office, else the default. */
  payment_terms_type?: import('./nurseryAccounts').PaymentTermsType | null;
  payment_terms_days?: number | null;
  po_required?: boolean | null;
  credit_limit?: NumericValue | null;
  /** Pays before its orders are despatched (Account rules). NULL = no. */
  pay_before_despatch?: boolean | null;
  /** Pays a deposit on orders for trees not ready. NULL = no (a site follows its payer). */
  deposit_required?: boolean | null;
  /** Its own deposit %. NULL = the organisation's. */
  deposit_pct?: NumericValue | null;
  /** Freight on its invoices. NULL mode = the head office's, else the delivery zone. */
  freight_mode?: import('./nurseryAccounts').FreightMode | null;
  freight_flat?: NumericValue | null;
  freight_pct?: NumericValue | null;
  freight_label?: string | null;
  invoice_delivery?: import('./nurseryAccounts').InvoiceDelivery | null;
  /** Monthly statements: null follows invoice delivery (emailed invoices, emailed statements). */
  statement_delivery?: 'email' | 'none' | null;
  /** Where statements go; null = its email. */
  statement_email?: string | null;
  /** The farm its orders are despatched from. NULL = the head office's. */
  despatch_farm_id?: number | null;
  despatch_farm_name?: string | null;
  /** The cross dock its cross dock orders go through. NULL = the head office's, else its state's. */
  cross_dock_id?: number | null;
  cross_dock_name?: string | null;
  /** Its orders go through a retailer's cross dock. NULL = the head office's, else no. */
  uses_cross_dock?: boolean | null;
  /** The one list it is priced from. NULL = the head office's, else the default. */
  price_list_id?: number | null;
  /** The nursery's walk-in customer, for counter sales: priced as the website prices a shopper. */
  is_walk_in?: boolean;
  /** The customer's number in the legacy system (hh.factree), when it came from there. */
  customer_number?: string | null;
  /** Detail only: its contacts, main contact first. */
  contacts?: NurseryCustomerContact[];
  // Joined
  tier_name?: string | null;
  tier_discount_pct?: number | null;
  linked_org_name?: string | null;
  parent_name?: string | null;
  parent_code?: string | null;
  parent_tier_name?: string | null;
  site_count?: number;
  /** List only: its state (VIC, NSW…) from its delivery address, else any address, else its head office's. */
  state?: string | null;
  /** List only: the suburb of the address its state came from. */
  suburb?: string | null;
  /** Detail only: what it trades on once the head office's settings are counted. */
  account?: import('./nurseryAccounts').CustomerAccount | null;
}

/** One way a customer being entered matches one already there. */
export type CustomerMatchReason = 'name' | 'email' | 'phone' | 'abn' | 'address';

/** A customer that looks like the one being entered (POST /nursery/customers/matches). */
export interface NurseryCustomerMatch {
  id: number;
  name: string;
  customer_code: string | null;
  active: boolean;
  parent_customer_id: number | null;
  parent_name: string | null;
  email: string | null;
  phone: string | null;
  delivery_address: string | null;
  reasons: CustomerMatchReason[];
}

/** What is matched on: whatever of these has been typed. */
export interface CustomerMatchQuery {
  name?: string;
  email?: string;
  phone?: string;
  abn?: string;
  addresses?: { line1?: string; suburb?: string; postcode?: string }[];
  /** The customer being edited. */
  exclude_id?: number;
  /** The head office a site is being added under, whose phone and email a store shares. */
  parent_customer_id?: number;
}

export interface CreateCustomerPayload {
  name: string;
  linked_org_id?: number | null;
  customer_tier_id?: number | null;
  contact_name?: string;
  email?: string;
  phone?: string;
  abn?: string;
  billing_address?: string;
  delivery_address?: string;
  billing_same_as_delivery?: boolean;
  /** The whole address set; replaces what is stored. */
  addresses?: NurseryCustomerAddress[];
  notes?: string;
  parent_customer_id?: number | null;
  bills_to_parent?: boolean;
  customer_code?: string | null;
  payment_terms_type?: import('./nurseryAccounts').PaymentTermsType | null;
  payment_terms_days?: number | null;
  po_required?: boolean | null;
  credit_limit?: number | null;
  pay_before_despatch?: boolean | null;
  deposit_required?: boolean | null;
  deposit_pct?: number | null;
  freight_mode?: import('./nurseryAccounts').FreightMode | null;
  freight_flat?: number | null;
  freight_pct?: number | null;
  freight_label?: string | null;
  invoice_delivery?: import('./nurseryAccounts').InvoiceDelivery | null;
  /** Monthly statements: null follows invoice delivery (emailed invoices, emailed statements). */
  statement_delivery?: 'email' | 'none' | null;
  /** Where statements go; null = its email. */
  statement_email?: string | null;
  despatch_farm_id?: number | null;
  /** The cross dock its cross dock orders go through. null: the head office's, else its state's. */
  cross_dock_id?: number | null;
  /** Its orders go through a retailer's cross dock. null: the head office's, else no. */
  uses_cross_dock?: boolean | null;
  /** The one list it is priced from. null: the head office's, else the default. */
  price_list_id?: number | null;
}

// ─── Price lists (versioned + tiered) ───

export type NurseryPriceListStatus = 'draft' | 'active' | 'archived';

/** A price-list row prices a stock item or an umbrella product. */
export interface NurseryPriceListItem {
  id: number;
  price_list_id: number;
  stock_item_id: number | null;
  product_id?: number | null;
  product_name?: string | null;
  min_quantity: number;
  unit_price: NumericValue;
  created_at: string;
  updated_at: string;
  // Joined
  cultivar_name?: string;
  rootstock_name?: string | null;
  tree_type_code?: string;
  sku_code?: string | null;
}

export interface NurseryPriceList {
  id: number;
  organisation_id: number;
  name: string;
  customer_tier_id: number | null;
  currency: string;
  effective_from: string;
  effective_to: string | null;
  status: NurseryPriceListStatus;
  notes: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  /** A contract list for one customer and its sites, rather than a tier. */
  customer_id?: number | null;
  /** The list every customer not given another is priced from. One per organisation. */
  is_default?: boolean;
  // Joined
  tier_name?: string | null;
  customer_name?: string | null;
  item_count?: number;
  /** Customers given this list (not counting those on it as the default). */
  customer_count?: number;
  items?: NurseryPriceListItem[];
}

export interface PriceListItemPayload {
  stock_item_id?: number;
  product_id?: number;
  min_quantity?: number;
  unit_price: number;
}

export interface CreatePriceListPayload {
  name: string;
  customer_tier_id?: number | null;
  customer_id?: number | null;
  currency?: string;
  effective_from: string;
  effective_to?: string | null;
  status?: NurseryPriceListStatus;
  notes?: string;
  items?: PriceListItemPayload[];
}

// ─── Licensed cultivars (stock-item picker source) ───

export interface LicensedCultivar {
  id: number;
  cultivar_name: string;
  trade_name: string | null;
  protection_status: string | null;
  /** Set on the cultivar page. A 'dual' cultivar belongs in both pickers. */
  cultivar_role?: 'scion' | 'rootstock' | 'dual' | null;
  /**
   * How this org came to be allowed to propagate it: a propagation licence
   * naming the cultivar, a signed master sublicence with the cultivar ticked
   * for this nursery, or no licence needed at all.
   */
  source: 'direct_licence' | 'master_sublicence' | 'open';
  /** Null for an open cultivar, which is propagable without any agreement. */
  license_id: number | null;
  license_number: string | null;
  is_open: boolean;
}

// ─── Location / Lot / Movement (production core) ───

export type RowSide = 'left' | 'right';

export type NurseryLocationType =
  | 'seed_bed' | 'nursery_row' | 'polyhouse' | 'container_area' | 'field' | 'other'
  /** Bare-root trees between digging and despatch (bare-root plan, 2026-10-12). */
  | 'heel_in' | 'coolstore'
  /** Places in a shop (retail plan, 2026-10-12). */
  | 'shop_floor' | 'storeroom'
  /** Where loads are treated under a quarantine protocol (ICA plan, 2026-10-13). */
  | 'quarantine_area';

/** What each kind of place is called. */
export const LOCATION_TYPE_LABEL: Record<NurseryLocationType, string> = {
  seed_bed: 'Seed bed', nursery_row: 'Row position', polyhouse: 'Polyhouse',
  container_area: 'Container area', field: 'Field', other: 'Other',
  heel_in: 'Heel-in', coolstore: 'Coolstore',
  shop_floor: 'Shop floor', storeroom: 'Storeroom',
  quarantine_area: 'Quarantine spray area',
};

export interface NurseryLocation {
  id: number;
  organisation_id: number;
  name: string;
  location_type: NurseryLocationType;
  farm_id: number | null;
  block_id: number | null;
  row_number: number | null;
  row_side?: RowSide | null;
  sequence_number: number | null;
  boundary_geometry: unknown | null;
  center_point: unknown | null;
  area_hectares: Numeric;
  capacity: number | null;
  row_spacing_meters: Numeric;
  plant_spacing_meters: Numeric;
  notes: string | null;
  active: boolean;
  /** Legacy: a despatch area before despatchable places (2026-10-07). Still holds any tree. */
  is_despatch_area?: boolean;
  created_at: string;
  updated_at: string;
  // joined
  block_name?: string;
  farm_name?: string;
  /** The farm it is on, its own or its block's. */
  place_farm_id?: number | null;
}

export interface CreateLocationPayload {
  name: string;
  location_type: NurseryLocationType;
  farm_id?: number | null;
  block_id?: number | null;
  row_number?: number | null;
  row_side?: RowSide | null;
  sequence_number?: number | null;
  capacity?: number | null;
  row_spacing_meters?: number | null;
  plant_spacing_meters?: number | null;
  notes?: string | null;
}

export interface CreateRowSlotsPayload {
  block_id: number;
  /** Every row from start_row to end_row. */
  start_row: number;
  end_row: number;
  /** Either side of a track through the block; empty for a block with none. */
  sides?: RowSide[];
  sequences: number[];
  capacity?: number | null;
  row_spacing_meters?: number | null;
  plant_spacing_meters?: number | null;
}

export type OriginCorner = 'bottom_left' | 'bottom_right' | 'top_left' | 'top_right';

export interface NurseryBlockLayout {
  id: number | null;
  organisation_id?: number;
  block_id: number;
  block_name?: string;
  farm_id?: number;
  farm_name?: string;
  heading_degrees: Numeric;
  origin_corner: OriginCorner;
  default_row_spacing_meters: Numeric;
  default_plant_spacing_meters: Numeric;
  notes: string | null;
  boundary_geometry?: unknown | null;
  center_point?: unknown | null;
}

export interface BlockLayoutPayload {
  heading_degrees?: number | null;
  origin_corner?: OriginCorner;
  default_row_spacing_meters?: number | null;
  default_plant_spacing_meters?: number | null;
  notes?: string | null;
}

export type LotStage =
  | 'sown' | 'seedling'
  /** Propagation: cuttings struck, rooted (cuttings, plantlets, layers), plantlets out of the flask. */
  | 'struck' | 'rooted' | 'deflasked'
  | 'transplanted' | 'grafted' | 'budded'
  | 'growing' | 'potted' | 'ready' | 'dispatched';

export type LotStatus = 'active' | 'depleted' | 'sold' | 'culled' | 'archived';

export interface NurseryLot {
  id: number;
  organisation_id: number;
  lot_code: string | null;
  cultivar_id: number;
  rootstock_id: number | null;
  tree_type_id: number;
  location_id: number | null;
  current_stage: LotStage;
  status: LotStatus;
  plant_date: string | null;
  graft_date: string | null;
  bud_date: string | null;
  pot_date: string | null;
  ready_date: string | null;
  ready_date_source: 'auto' | 'manual';
  dispatched_date: string | null;
  initial_count: number;
  current_count: number;
  notes: string | null;
  /** The stock item it is. Absent from an older server. */
  stock_item_id?: number | null;
  /** 'merchandise' for a lot of potting mix or pots, whose cultivar_name is the item's name. */
  item_kind?: StockItemKind | null;
  item_unit?: string | null;
  /** Bought in: what one cost, ex GST, landed. Null for a lot we grew. */
  unit_cost?: Numeric;
  received_on?: string | null;
  supplier_id?: number | null;
  /** Set when graded; null while growing. */
  grade_rank?: number | null;
  /** The most refined count the lot has had. */
  last_count_kind?: CountKind | null;
  last_counted_at?: string | null;
  /** A batch in trays or flasks: what kind, and how many. */
  container_type_id?: number | null;
  container_count?: number | null;
  /** A stool bed or stock plants: material is taken from it without reducing it, and it is never ready stock. */
  is_mother?: boolean;
  created_at: string;
  updated_at: string;
  // joined
  supplier_name?: string | null;
  container_type_name?: string | null;
  cells_per_container?: number | null;
  tree_type_purpose?: TreeTypePurpose;
  cultivar_name?: string;
  crop_type_id?: number | null;
  crop_type_name?: string | null;
  rootstock_name?: string | null;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  readiness_mode?: ReadinessMode;
  location_name?: string | null;
  location_type?: NurseryLocationType | null;
  row_number?: number | null;
  row_side?: RowSide | null;
  sequence_number?: number | null;
  block_id?: number | null;
  block_name?: string | null;
  farm_id?: number | null;
  farm_name?: string | null;
}

/** A plant lot names its cultivar and tree type; a merchandise lot only its stock item. */
export interface CreateLotPayload {
  lot_code?: string | null;
  cultivar_id?: number;
  rootstock_id?: number | null;
  tree_type_id?: number;
  stock_item_id?: number;
  location_id?: number | null;
  /** No location: the lot goes in this farm's or block's "Row not set" place. */
  farm_id?: number | null;
  block_id?: number | null;
  current_stage?: LotStage;
  plant_date?: string | null;
  graft_date?: string | null;
  bud_date?: string | null;
  pot_date?: string | null;
  ready_date?: string | null;
  ready_date_source?: 'auto' | 'manual';
  initial_count?: number;
  notes?: string | null;
  source_supplier?: string | null;
  source_ref?: string | null;
  source_lot_id?: number | null;
  source_cultivar_id?: number | null;
  record_origin?: boolean;
  unit_cost?: number | null;
  received_on?: string | null;
  supplier_id?: number | null;
  container_type_id?: number | null;
  container_count?: number | null;
  is_mother?: boolean;
}

export type MovementEventType =
  | 'sow' | 'transplant' | 'pot_up' | 'graft' | 'bud' | 'split'
  | 'grade' | 'dig' | 'ship' | 'sell' | 'cull' | 'adjust'
  /** Picking takes trees off a lot; a pick undone or lowered puts them back. */
  | 'pick' | 'unpick' | 'transfer'
  /** Recorded by hand on the lot itself: a change of stage, or out of / back into stock. */
  | 'stage' | 'archive' | 'restore'
  /**
   * Propagation. strike: material into a struck batch. deflask: plantlets out
   * of the flask. harvest: rooted layers from a mother, which stays as it was.
   * receive: bought in on a purchase order. root: struck/deflasked to rooted,
   * or sown to seedlings, with the count that took.
   */
  | 'strike' | 'deflask' | 'harvest' | 'receive' | 'root';

export interface NurseryMovement {
  id: number;
  organisation_id: number;
  event_type: MovementEventType;
  from_lot_id: number | null;
  to_lot_id: number | null;
  quantity: number;
  movement_date: string;
  source_supplier: string | null;
  source_ref: string | null;
  source_lot_id: number | null;
  source_cultivar_id: number | null;
  to_location_id: number | null;
  new_ready_date: string | null;
  /** The stages a 'stage' movement moved the lot between. */
  from_stage?: LotStage | null;
  to_stage?: LotStage | null;
  propagation_method_id?: number | null;
  notes: string | null;
  recorded_by_user_id: number | null;
  created_at: string;
  recorded_by_name?: string;
}

/** Stages a person can put a lot at; 'dispatched' is what despatch does. */
export const MANUAL_LOT_STAGES: LotStage[] = ['sown', 'seedling', 'struck', 'rooted', 'deflasked', 'transplanted',
  'grafted', 'budded', 'growing', 'potted', 'ready'];

/** What each stage is called on screen. 'ready' is what puts trees in free stock. */
export const LOT_STAGE_LABELS: Record<LotStage, string> = {
  sown: 'Sown', seedling: 'Seedling', struck: 'Struck', rooted: 'Rooted', deflasked: 'Deflasked',
  transplanted: 'Transplanted', grafted: 'Grafted', budded: 'Budded',
  growing: 'Growing', potted: 'Potted', ready: 'Ready to sell', dispatched: 'Dispatched',
};

/** What a change to several lots did: the ones changed, and why the rest were not. */
export interface LotChangeResult {
  changed: number[];
  skipped: { id: number; reason: string }[];
}

export interface MovementGradingDetail {
  grading_pass?: number;
  count_before?: number;
  first_grade_count?: number;
  second_grade_count?: number;
  cull_count?: number;
  notes?: string;
}

export interface ApplyMovementPayload {
  event_type: MovementEventType;
  from_lot_id?: number | null;
  to_lot_id?: number | null;
  quantity: number;
  movement_date: string;
  cultivar_id?: number;
  rootstock_id?: number | null;
  tree_type_id?: number;
  /** The stock item the new lot is to be, in place of the triple. */
  to_stock_item_id?: number | null;
  container_type_id?: number | null;
  container_count?: number | null;
  propagation_method_id?: number | null;
  lot_code?: string | null;
  to_location_id?: number | null;
  source_supplier?: string | null;
  source_ref?: string | null;
  source_lot_id?: number | null;
  source_cultivar_id?: number | null;
  new_ready_date?: string | null;
  notes?: string | null;
  grading?: MovementGradingDetail;
}

export interface LotLineageNode {
  id: number;
  lot_code: string | null;
  current_stage: LotStage;
  status: LotStatus;
  current_count: number;
  ready_date: string | null;
  cultivar_name: string;
  tree_type_code: string;
  location_name: string | null;
}

export interface NurseryLotLineage {
  lots: LotLineageNode[];
  movements: NurseryMovement[];
}

export type ProductionRunType = 'dig' | 'ship' | 'grade' | 'pot' | 'custom';
export type ProductionRunStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled';

export interface NurseryProductionRunLot {
  run_id: number;
  lot_id: number;
  quantity: number | null;
  done: boolean;
  lot_code?: string | null;
  current_stage?: LotStage;
  current_count?: number;
  ready_date?: string | null;
  cultivar_name?: string;
  tree_type_code?: string;
  location_name?: string | null;
}

export interface NurseryProductionRun {
  id: number;
  organisation_id: number;
  run_type: ProductionRunType;
  name: string;
  scheduled_date: string | null;
  status: ProductionRunStatus;
  filter_snapshot: unknown | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  lot_count?: number;
  total_quantity?: number;
  lots?: NurseryProductionRunLot[];
}

export interface CreateProductionRunPayload {
  run_type: ProductionRunType;
  name: string;
  scheduled_date?: string | null;
  filter_snapshot?: unknown | null;
  notes?: string | null;
  lots?: Array<number | { lot_id: number; quantity?: number | null }>;
}

export interface ProductionBoardFilters {
  stock_type?: 'B' | 'P';
  stage?: LotStage;
  cultivar_id?: number;
  crop_type_id?: number;
  ready_from?: string;
  ready_to?: string;
  location_type?: NurseryLocationType;
  farm_id?: number;
  block_id?: number;
  row_number?: number;
  row_side?: RowSide | 'none';
  program_id?: number;
}

/** Where lots stand: a farm, a block in it, a row in that, one side of a track. */
export interface PlaceFilter {
  farm_id?: number;
  block_id?: number;
  row_number?: number;
  /** 'none' is the row with no side, in a block where other rows have one. */
  row_side?: RowSide | 'none';
}

export interface NurseryInventoryRow {
  id: number;
  organisation_id: number;
  cultivar_id: number;
  rootstock_id: number | null;
  tree_type_id: number;
  amt_growing: number;
  amt_ready: number;
  amt_on_order: number;
  amt_allocated: number;
  amt_picked: number;
  amt_shipped: number;
  est_avail_count: number;
  updated_at: string;
  cultivar_name?: string;
  rootstock_name?: string | null;
  tree_type_code?: string;
  tree_type_name?: string;
  stock_type?: 'B' | 'P';
  cultivar_protection_status?: string;
}

export interface NurseryDashboardStatsV2 {
  active_lots: number;
  total_trees: number;
  ready_now: number;
  ready_30d: number;
  ready_90d: number;
  by_stage: Array<{ current_stage: LotStage; lots: number; trees: number }>;
}

/**
 * What cancelling an order would do, and what stops it
 * (GET /nursery/orders/:id/cancellation). The cancel runs the same check.
 */
export interface OrderCancellation {
  order_id: number;
  order_number: string;
  status: NurseryOrderStatus;
  can_cancel: boolean;
  /** Sentences, each saying what stops it and what to do instead. */
  blockers: string[];
  /** Per lot: trees going back to free stock, and how many of those were already picked. */
  lots: { lot_id: number; lot_code: string | null; allocated: number; picked: number }[];
  trees_released: number;
  trees_picked: number;
  load: { id: number; load_number: string; status: NurseryLoadStatus } | null;
  /** Paid online: refunded to the card, in full. */
  refund_amount: number | null;
  /** Paid in cash at the counter: the refund is handed back from a till. Absent from an older server. */
  refund_method?: 'card' | 'cash';
  /** Still at Stripe checkout; a payment that lands later is refunded. */
  payment_pending: boolean;
  /** Its own backorders, which are not cancelled with it. */
  backorders: { id: number; order_number: string; status: NurseryOrderStatus }[];
}

/**
 * Update prices on an order (GET /nursery/orders/:id/reprice): each line
 * against today's price book. `fill` has no price and the book now has one;
 * `change` differs from the book, and moves only when asked; `no_price` the
 * book still does not price.
 */
export interface OrderRepriceLine {
  line_id: number;
  description: string;
  quantity: number;
  discount_pct: number;
  unit_price: number | null;
  proposed_price: number | null;
  source_label: string | null;
  price_list_name: string | null;
  change: 'fill' | 'change' | 'same' | 'no_price';
}

export interface OrderReprice {
  can_reprice: boolean;
  blocker: string | null;
  invoice: { id: number; invoice_number: string; status: string } | null;
  lines: OrderRepriceLine[];
  to_fill: number;
  to_change: number;
  still_unpriced: number;
}

export interface OrderRepriceResult {
  updated: number;
  still_unpriced: number;
  /** The draft invoice written again from the new prices. */
  invoice_rebuilt: string | null;
}

export interface OrderCancelResult extends OrderCancellation {
  refund: { id: number; status: 'pending' | 'succeeded' | 'failed'; amount: NumericValue; error?: string | null } | null;
}

// ─── Forecast: what the growing crop is expected to give ───

/** 1 bud count (or none), 2 first count, 3 second count, 4 graded or ready. */
export type ForecastConfidence = 1 | 2 | 3 | 4;

export interface ForecastRow {
  cultivar_id: number;
  cultivar_name: string;
  rootstock_id: number | null;
  rootstock_name: string | null;
  tree_type_id: number;
  tree_type_code: string;
  tree_type_name: string;
  stock_type: 'B' | 'P';
  stock_item_id: number | null;
  /** The year the sale season starts; null when the lots have no ready date. */
  season: number | null;
  lots: number;
  trees_counted: number;
  expected: number;
  confidence: ForecastConfidence;
  /** grade_rank → expected trees. Empty for an ungraded tree type. */
  by_grade: Record<string, number>;
  graded_trees: number;
  counts: { none: number; bud_count: number; first_count: number; second_count: number; graded: number };
}

export interface ForecastResponse {
  seasons: number[];
  rows: ForecastRow[];
  /** tree_type_id → its grades' names. */
  grades: Record<string, { grade_rank: number; name: string }[]>;
}

// ── Drawing down a bulk order (HHNodeServer nurseryOrderDrawdownService) ──

/** One draw-down taken off a bulk order. */
export interface OrderDrawdown {
  order_id: number;
  order_number: string;
  status: NurseryOrderStatus;
  drawn_at: string;
  trees: number;
  drawn_by: string | null;
}

/** A customer's order not yet being picked, offered on the order sheet. */
export interface CustomerOpenOrder {
  id: number;
  order_number: string;
  order_date: string;
  status: NurseryOrderStatus;
  customer_po: string | null;
  held_for_drawdown: boolean;
  despatch_by: string | null;
  requested_delivery_date: string | null;
  total_trees: number;
  line_count: number;
  drawn_trees: number;
  load_number: string | null;
  load_status: NurseryLoadStatus | null;
  /** Confirmed, not yet being picked, with trees left on it. */
  can_draw: boolean;
}

export interface DrawDownPayload {
  lines: { line_id: number; quantity: number }[];
  customer_po?: string | null;
  despatch_by?: string | null;
  notes?: string | null;
  load_id?: number | null;
}

export interface DrawDownResult {
  order_id: number;
  order_number: string;
  trees: number;
  /** Trees already allocated on the bulk order that went with the draw-down. */
  allocated_moved: number;
  /** Everything has now been drawn off the bulk order. */
  source_completed: boolean;
  load_id: number | null;
}
